import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, ChevronDown, ChevronUp } from "lucide-react";
import { THEME } from "@/constants/testIds";
import CountryFlag from "@/components/CountryFlag";
import CommentSection from "@/components/CommentSection";

const accentClasses = {
    green: "before:bg-brand-green",
    blue: "before:bg-brand-blue",
    gold: "before:bg-brand-gold",
};

export default function ContributionCard({ contribution, index = 0 }) {
    const accent = accentClasses[contribution.accent] || accentClasses.green;
    const [commentsOpen, setCommentsOpen] = useState(false);

    return (
        <motion.article
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: Math.min(index * 0.04, 0.4), ease: [0.22, 1, 0.36, 1] }}
            data-testid={THEME.contributionCard(contribution.id)}
            className={`relative card-flat overflow-hidden before:content-[''] before:absolute before:left-0 before:top-8 before:bottom-8 before:w-[3px] before:rounded-full ${accent}`}
        >
            <div className="flex items-center justify-between mb-4">
                <span className="inline-flex items-center gap-2 caption text-brand-green">
                    <CountryFlag country={contribution.country} size="md" />
                    {contribution.country}
                </span>
                <span className="font-mono text-[10px] text-ink-muted">
                    #{contribution.theme}
                </span>
            </div>

            <h3 className="heading-serif text-[26px] md:text-3xl text-ink-primary leading-tight mb-2">
                {contribution.student_name}
            </h3>
            <p className="text-sm text-ink-muted mb-5 italic">{contribution.school}</p>

            <p className="text-ink-secondary text-[17px] leading-relaxed whitespace-pre-line">
                {contribution.text}
            </p>

            <button
                type="button"
                onClick={() => setCommentsOpen((v) => !v)}
                data-testid={`toggle-comments-${contribution.id}`}
                className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] font-semibold text-brand-blue hover:text-brand-green transition-colors"
            >
                <MessageSquare size={14} strokeWidth={1.6} />
                {commentsOpen ? "Hide comments" : "Comments & files"}
                {commentsOpen ? (
                    <ChevronUp size={14} strokeWidth={1.6} />
                ) : (
                    <ChevronDown size={14} strokeWidth={1.6} />
                )}
            </button>

            <AnimatePresence initial={false}>
                {commentsOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                    >
                        <CommentSection contributionId={contribution.id} />
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.article>
    );
}
